/**
 * WebView 환경에서 API 서버와 통신하는 서비스
 */

import { isWebView, isAppleVisionPro } from "../utils/webViewUtils";
import type { UserInfo } from "../utils/webViewUtils";

// API 서버 기본 URL
const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

/**
 * WebView에서 토큰을 API 서버로 전송하여 검증
 */
export const verifyTokenWithServer = async (
  token: string,
  userInfo: UserInfo | null = null,
): Promise<unknown | null> => {
  try {
    if (!isWebView()) {
      console.log("🔄 일반 브라우저 환경 - 서버 검증 건너뜀");
      return null;
    }

    console.log("🔄 WebView 토큰 서버 검증 시작");

    const platform = isAppleVisionPro() ? "visionos" : "webview";

    // 토큰 검증 API 호출
    const response = await fetch(
      `${API_BASE_URL}/api/users/webview/verify-token?token=${encodeURIComponent(
        token,
      )}&platform=${platform}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    if (!response.ok) {
      throw new Error(`서버 검증 실패: ${response.status}`);
    }

    const result = await response.json();
    console.log("✅ WebView 토큰 서버 검증 성공:", result);

    return result;
  } catch (error) {
    console.error("❌ WebView 토큰 서버 검증 실패:", error);
    return null;
  }
};

/**
 * WebView에서 로그인 성공을 API 서버에 알림
 */
export const notifyAuthSuccessToServer = async (
  token: string,
  userInfo: UserInfo,
): Promise<unknown | null> => {
  try {
    if (!isWebView()) {
      if (process.env.NODE_ENV === "development") {
        console.log("🔄 일반 브라우저 환경 - 서버 알림 건너뜀");
      }
      return null;
    }

    console.log("🔄 WebView 로그인 성공 서버 알림 시작");

    const platform = isAppleVisionPro() ? "visionos" : "webview";

    const params = new URLSearchParams({
      token: token,
      uid: userInfo.uid,
      email: userInfo.email || "",
      provider: userInfo.provider || "unknown",
      platform: platform,
    });

    // 로그인 성공 알림 API 호출
    const response = await fetch(
      `${API_BASE_URL}/api/users/webview/auth-success?${params}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    if (!response.ok) {
      throw new Error(`서버 알림 실패: ${response.status}`);
    }

    const result = await response.json();
    console.log("✅ WebView 로그인 성공 서버 알림 완료:", result);

    return result;
  } catch (error) {
    console.error("❌ WebView 로그인 성공 서버 알림 실패:", error);
    return null;
  }
};

/**
 * WebView 환경에서 토큰을 사용하여 API 요청 수행
 */
export const makeWebViewApiRequest = async (
  endpoint: string,
  token: string,
  options: RequestInit = {},
): Promise<unknown | null> => {
  try {
    if (!isWebView()) {
      if (process.env.NODE_ENV === "development") {
        console.log(
          `🔄 일반 브라우저 환경 - WebView API 요청 건너뜀: ${endpoint}`,
        );
      }
      return null;
    }

    console.log(`🔄 WebView API 요청: ${endpoint}`);

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        ...options.headers,
      },
    });

    if (!response.ok) {
      throw new Error(`API 요청 실패: ${response.status}`);
    }

    const result = await response.json();
    console.log(`✅ WebView API 요청 성공: ${endpoint}`, result);

    return result;
  } catch (error) {
    console.error(`❌ WebView API 요청 실패: ${endpoint}`, error);
    return null;
  }
};

/**
 * WebView 환경에서 사용자 정보 조회
 */
export const getWebViewUserInfo = async (
  token: string,
): Promise<unknown | null> => {
  return await makeWebViewApiRequest("/api/users/user/profile", token);
};

/**
 * WebView 환경에서 사용자 파일 목록 조회
 */
export const getWebViewUserFiles = async (
  token: string,
  userId: string,
): Promise<unknown | null> => {
  return await makeWebViewApiRequest(`/api/users/${userId}/viewr-files`, token);
};
